import { useEffect, useState } from "react";
import { ChevronDown, FileSpreadsheet, FileText, MessageCircle, Pencil, UserRound } from "lucide-react";
import { BILLING_ON, fetchBillingFirms, fetchProformas, proformaParts, ron } from "@/lib/bf";
import BFSendBilling from "./BFSendBilling";
import BFPfEdit from "./BFPfEdit";

const waNum = (p) => { const d = (p || "").replace(/\D/g, ""); return d.length < 9 ? "" : d.startsWith("07") ? `4${d}` : d; };

const Row = ({ r, mine, firms, update }) => {
  const [edit, setEdit] = useState(false);
  const isAdmin = !mine;
  const canEdit = !r.preview && (isAdmin || (!r.locked && !r.billing));
  return (
  <li data-testid={`bf-pf-${r.no}`} className={edit ? "is-editing" : ""}>
    <div>
      <b>{r.no} · {r.salon || "Salon neînregistrat"}{r.preview && <em className="bfa-pf-tag">test</em>}{r.locked && <em className="bfa-pf-tag is-locked" data-testid={`bf-pf-locked-${r.no}`}>la facturare</em>}{r.edited && !r.locked && <em className="bfa-pf-tag">modificată</em>}</b>
      <span>{r.date}{r.cui ? ` · CUI ${r.cui}` : ""}{!mine ? ` · agent: ${r.agent || "fără agent"}` : ""} · {r.pieces} buc.</span>
    </div>
    <div className="interested-actions">
      {canEdit && !edit && <button type="button" className="st-btn st-btn--light" onClick={() => setEdit(true)} data-testid={`bf-pf-edit-${r.no}`}><Pencil size={14} /> Modifică</button>}
      {BILLING_ON && <BFSendBilling row={r} firms={firms} onDone={update} />}
      <span className="bfa-pf-total" data-testid={`bf-pf-total-${r.no}`}>{ron(r.total)}</span>
      {waNum(r.phone) && (
        <a className="st-btn st-btn--ghost" target="_blank" rel="noopener noreferrer" data-testid={`bf-pf-wa-${r.no}`}
          href={`https://wa.me/${waNum(r.phone)}?text=${encodeURIComponent(`Bună ziua! Proforma ${r.no} pentru ${r.salon || "salonul dvs."}, în valoare de ${ron(r.total)}:\n${proformaParts(r)[0].xlsx}`)}`}>
          <MessageCircle size={14} /> WhatsApp
        </a>
      )}
      {proformaParts(r).map((p) => (
        <span key={p.no} className="bfa-pf-part">
          <a className="st-btn st-btn--ghost" href={p.xlsx} data-testid={`bf-pf-xlsx-${p.no}`}><FileSpreadsheet size={14} /> Excel{p.tag || ""}</a>
        </span>
      ))}
    </div>
    {edit && canEdit && <BFPfEdit r={r} onSaved={update} onClose={() => setEdit(false)} />}
  </li>
  );
};

const Group = ({ name, rows, open, toggle, ...rest }) => {
  const real = rows.filter((r) => !r.preview);
  const id = name.replace(/\W+/g, "-").toLowerCase();
  return (
    <div className={`bf-pf-group ${open ? "is-open" : ""}`} data-testid={`bf-pf-group-${id}`}>
      <button type="button" className="bf-pf-group-head" onClick={toggle} aria-expanded={open} data-testid={`bf-pf-group-toggle-${id}`}>
        <UserRound size={16} /><b>{name}</b>
        <span>{real.length} {real.length === 1 ? "proformă" : "proforme"}{rows.length > real.length ? ` (+${rows.length - real.length} test)` : ""} · {ron(real.reduce((s, r) => s + r.total, 0))}</span>
        <ChevronDown size={16} className="bf-pf-chev" />
      </button>
      {open && <ul className="bfa-leads">{rows.map((r) => <Row key={r.id} r={r} {...rest} />)}</ul>}
    </div>
  );
};

export default function BFProformas({ mine = false }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [firms, setFirms] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => {
    fetchProformas(mine).then(setRows).catch(() => setRows("err"));
    if (BILLING_ON) fetchBillingFirms().then(setFirms).catch(() => {});
  }, [mine]);
  const update = (nr) => setRows(rows.map((r) => (r.id === nr.id ? nr : r)));
  if (!rows) return <p className="st-hint" data-testid="bf-pf-loading">Se încarcă proformele…</p>;
  if (rows === "err") return <p className="st-hint" data-testid="bf-pf-error">Nu am putut încărca proformele. Reîncarcă pagina.</p>;
  const f = q.trim().toLowerCase();
  const list = f ? rows.filter((r) => [r.no, r.salon, r.cui, r.agent].some((x) => x?.toLowerCase().includes(f))) : rows;
  const real = rows.filter((r) => !r.preview);
  const groups = Object.entries(list.reduce((a, r) => { const k = r.agent || "Fără agent"; (a[k] = a[k] || []).push(r); return a; }, {}))
    .sort((a, b) => (a[0] === "Fără agent") - (b[0] === "Fără agent") || b[1].length - a[1].length);
  const props = { mine, firms, update };
  return (
    <section className={mine ? "bf-agent-pf" : "invite-box"} data-testid={mine ? "bf-agent-proformas" : "bfa-proformas"}>
      <h3 data-testid="bf-pf-title">{mine ? "Proformele saloanelor tale" : "Proforme"} ({real.length}) · {ron(real.reduce((s, r) => s + r.total, 0))}</h3>
      {rows.length > 4 && <input className="bfa-pf-search" placeholder="Caută după număr, salon, CUI sau agent" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bf-pf-search" />}
      {!rows.length && <p className="st-hint" data-testid="bf-pf-empty">Încă nu s-a emis nicio proformă. Se creează automat când un salon trimite comanda.</p>}
      {mine ? <ul className="bfa-leads">{list.map((r) => <Row key={r.id} r={r} {...props} />)}</ul> : (
        <div className="bf-pf-groups">
          {groups.map(([name, rs]) => <Group key={name} name={name} rows={rs} open={!!f || !!open[name]} toggle={() => setOpen({ ...open, [name]: !open[name] })} {...props} />)}
        </div>
      )}
    </section>
  );
}
